import PropTypes from 'prop-types'
import { useCallback, useEffect, useRef, useState } from 'react'

import { MENSAJE_TIPO_PREGUNTA } from '../../../constants/mensajesTipos.js'
import { ESTADO_PUBLICADA, SIN_PREGUNTAS_TEXTO } from '../../../constants/publicaciones'
import { toastErrorLog } from '../../../helpers/toastErrorLog'
import { useAuth } from '../../../hooks/useAuth'
import { formatearFecha } from '../../helpers/helper'
import createValidador from '../../hooks/createValidador'
import { adjuntoBuilder } from '../../reutilizables/upload/adjuntoBuilder'
import { ModalUpload } from '../../reutilizables/upload/ModalUpload'
import * as archivoAdjuntoService from '../../servicios/archivoAdjuntoService'
import { insertarMail } from '../../servicios/mailsServices'
import * as mensajesService from '../../servicios/mensajesService'
import Input from '../../ui/Input'
import { Adjuntos } from './Adjuntos'
import styles from './Mensajeria.module.css'

const LENGTH_PREGUNTA = 500

const Mensajeria = ({
  idPublicacion,
  mensajes,
  setMensajes,
  tipo,
  vendedorNombre,
  esVendedor,
  idVendedor,
  idCompradorFiltro,
  idPublicacionEstado
}) => {
  const [textosRespuesta, setTextosRespuesta] = useState({})
  const [textoPregunta, setTextoPregunta] = useState('')
  const [errores, setErrores] = useState({})
  const [adjunto, setAdjunto] = useState({})
  const [adjuntoPendiente, setAdjuntoPendiente] = useState({})
  const [mensajeActivoId, setMensajeActivoId] = useState(null)
  const [modalRespId, setModalRespId] = useState(null)
  const [showModalNueva, setShowModalNueva] = useState(false)
  const [enviando, setEnviando] = useState(false)

  const enviandoRef = useRef(false)
  const adjuntosPedidosRef = useRef(new Set())

  const { loginDataContext: { idUsuario: usuario } = {} } = useAuth()
  const { Validador } = createValidador()

  const esquemaConTexto = {
    texto: Validador.Kernel.string()
      .trim()
      .required()
      .max(LENGTH_PREGUNTA)
      .label('Mensaje')
      .error((errors) => Validador.mensaje(errors))
  }

  const esquemaSoloLongitud = {
    texto: Validador.Kernel.string()
      .trim()
      .allow('')
      .max(LENGTH_PREGUNTA)
      .label('Mensaje')
      .error((errors) => Validador.mensaje(errors))
  }

  const manejarAdjunto = useCallback(async (data, setTarget, idKey, idConocido) => {
    const { dataAdjunto, adjunto } = data
    const previewAdjunto = await adjuntoBuilder(dataAdjunto, adjunto, adjunto.archivo, {
      preview: true
    })
    setTarget((prev) => {
      const actuales = prev?.[idKey] || []
      const id = adjunto.idAdjunto || data.idAdjunto || idConocido
      if (actuales.some((a) => a.idAdjunto === id)) return prev
      return { ...prev, [idKey]: [...actuales, { ...previewAdjunto, idAdjunto: id }] }
    })
  }, [])

  useEffect(() => {
    const cargarAdjuntos = (ids, destino) => {
      ids.split(',').forEach((idCrudo) => {
        const id = idCrudo.trim()
        const clave = `${destino}:${id}`
        if (!id || adjuntosPedidosRef.current.has(clave)) return
        adjuntosPedidosRef.current.add(clave)
        archivoAdjuntoService
          .buscarAdjuntosPorId(id)
          .then(({ data }) => manejarAdjunto(data, setAdjunto, destino, id))
          .catch(() => {
            adjuntosPedidosRef.current.delete(clave)
          })
      })
    }

    mensajes.forEach((m) => {
      if (m.idsAdjuntos) cargarAdjuntos(m.idsAdjuntos, m.idMensaje)
      if (m.idsAdjuntosRta) cargarAdjuntos(m.idsAdjuntosRta, `rta-${m.idMensaje}`)
    })
  }, [mensajes, manejarAdjunto])

  const submitRespuesta = async (idMensajePadre, padre) => {
    if (enviandoRef.current) return
    const value = (textosRespuesta[idMensajePadre] || '').trim()
    const tieneAdjuntos = !!adjuntoPendiente?.[`resp-${idMensajePadre}`]?.length
    const esquema = tieneAdjuntos ? esquemaSoloLongitud : esquemaConTexto
    const error = Validador.validarDato(esquema, { texto: value }, 'texto')
    if (error) {
      setErrores(error)
      return
    }
    setErrores({})

    const idUsuarioChat = padre.idComprador

    const mensajeRes = {
      idMensajePadre,
      idPublicacion,
      idPedidoItem: padre.idPedidoItem,
      idPresupuesto: padre.idPresupuesto,
      idUsuarioChat,
      idUsuarioEmisor: usuario,
      idComprador: padre.idComprador,
      idVendedor: padre.idVendedor,
      texto: value,
      privado: padre.privado ?? false,
      idMensajeTipo: MENSAJE_TIPO_PREGUNTA,
      IdsAdjuntosList: adjuntoPendiente?.[`resp-${idMensajePadre}`]?.map((a) => a.idAdjunto) ?? null
    }

    enviandoRef.current = true
    setEnviando(true)
    try {
      await mensajesService.insertar(mensajeRes)
      const adjuntosRespuesta = adjuntoPendiente?.[`resp-${idMensajePadre}`] || []
      setMensajes((prev) =>
        prev.map((m) => {
          if (m.idMensaje !== idMensajePadre) return m
          return {
            ...m,
            textoRta: value,
            fechaEscritoRta: new Date().toISOString(),
            idVendedorEmisorRta: usuario
          }
        })
      )
      if (adjuntosRespuesta.length > 0) {
        setAdjunto((prev) => ({ ...prev, [`rta-${idMensajePadre}`]: adjuntosRespuesta }))
      }
      setTextosRespuesta((prev) => ({ ...prev, [idMensajePadre]: '' }))
      setAdjuntoPendiente((prev) => {
        const updated = { ...prev }
        delete updated[`resp-${idMensajePadre}`]
        return updated
      })
      insertarMail({
        idTo: idUsuarioChat.toString(),
        idTipo: 2,
        param2: 'en la publicación',
        param3: idPublicacion?.toString() ?? ''
      }).catch(() => {})
    } catch (error) {
      toastErrorLog(error, 'Ocurrió un error al enviar la respuesta.', 'mensajeria')
    } finally {
      enviandoRef.current = false
      setEnviando(false)
    }
  }

  const submitPregunta = async () => {
    if (enviandoRef.current) return
    const value = textoPregunta.trim()
    const tieneAdjuntos = !!adjuntoPendiente?.['nueva']?.length
    const esquema = tieneAdjuntos ? esquemaSoloLongitud : esquemaConTexto
    const error = Validador.validarDato(esquema, { texto: value }, 'texto')
    if (error) {
      setErrores(error)
      return
    }
    setErrores({})

    const mensajeRes = {
      idMensajePadre: null,
      idPublicacion,
      idPedidoItem: null,
      idPresupuesto: null,
      idUsuarioChat: idVendedor,
      idUsuarioEmisor: usuario,
      idComprador: usuario,
      idVendedor,
      texto: value,
      privado: false,
      idMensajeTipo: MENSAJE_TIPO_PREGUNTA,
      IdsAdjuntosList: adjuntoPendiente?.['nueva']?.map((a) => a.idAdjunto) ?? null
    }

    enviandoRef.current = true
    setEnviando(true)
    try {
      await mensajesService.insertar(mensajeRes)
      const idMensajeNuevo = Date.now()
      const adjuntosPregunta = adjuntoPendiente?.['nueva'] || []
      setMensajes((prev) => [
        ...prev,
        {
          ...mensajeRes,
          vendedor: vendedorNombre ?? mensajes[0]?.vendedor ?? '',
          comprador: mensajes[0]?.comprador ?? '',
          idMensaje: idMensajeNuevo,
          fechaEscrito: new Date().toISOString(),
          respuestas: []
        }
      ])
      if (adjuntosPregunta.length > 0) {
        setAdjunto((prev) => ({ ...prev, [idMensajeNuevo]: adjuntosPregunta }))
      }
      setTextoPregunta('')
      setAdjuntoPendiente((prev) => {
        const updated = { ...prev }
        delete updated['nueva']
        return updated
      })
      insertarMail({
        idTo: idVendedor?.toString() ?? '',
        idTipo: 1,
        param2: 'en la publicación',
        param3: idPublicacion?.toString() ?? ''
      }).catch(() => {})
    } catch (error) {
      toastErrorLog(error, 'Ocurrió un error al enviar la pregunta.', 'mensajeria')
    } finally {
      enviandoRef.current = false
      setEnviando(false)
    }
  }

  const renderMensaje = (msg) => {
    const yaRespondido = Boolean(msg.textoRta || msg.fechaEscritoRta || msg.idMensajeRta)
    const vistaChatFija = tipo === 'vendedor'
    const idEmisorRespuesta = Number(
      msg.idVendedorEmisorRta ?? msg.idUsuarioEmisorRta ?? msg.idUsuarioEmisor
    )
    const respondioTitularPublicacion = msg.idVendedorEmisorRta === msg.idVendedor
    const nombrePersonaVendedor = vendedorNombre ?? msg.nombreVendedor ?? ''
    const nombreEmpresaVendedor = msg.vendedor ?? nombrePersonaVendedor
    const nombreRespuesta = respondioTitularPublicacion
      ? nombrePersonaVendedor || nombreEmpresaVendedor
      : nombreEmpresaVendedor
    const esPreguntaPropia = vistaChatFija ? false : msg.idComprador === usuario
    const esRespuestaPropia = vistaChatFija ? true : idEmisorRespuesta === Number(usuario)

    return (
      <div key={msg.idMensaje} className={styles.preguntaItem}>
        <div
          className={`${styles.mensajeFila} ${!vistaChatFija && msg.idComprador === usuario ? styles.filaDerecha : ''}`}
        >
          <div className={`${styles.preguntaBox} ${esPreguntaPropia ? styles.mio : ''}`}>
            <div className={styles.cabeceraMensaje}>
              <span className={styles.preguntaLabel}>{msg.comprador} preguntó</span>
              <span
                className={`${styles.badgePrivacidad} ${msg.privado ? styles.badgePrivado : ''}`}
              >
                <i className={`fa ${msg.privado ? 'fa-lock' : 'fa-globe'}`} aria-hidden="true" />
                {msg.privado ? 'Privada' : 'Pública'}
              </span>
            </div>
            <p className={styles.texto}>{msg.texto}</p>
            <Adjuntos adjunto={adjunto?.[msg.idMensaje] ?? null} />
            <span className={styles.fecha}>{formatearFecha(msg.fechaEscrito, 'FH', 'hs')}</span>
          </div>
          {esVendedor && !yaRespondido && (
            <button
              type="button"
              className={`${styles.botonResponder} ${mensajeActivoId === msg.idMensaje ? styles.botonResponderActivo : ''}`}
              onClick={() =>
                setMensajeActivoId((prev) => (prev === msg.idMensaje ? null : msg.idMensaje))
              }
              title="Responder"
              aria-expanded={mensajeActivoId === msg.idMensaje}
            >
              <i className="fa fa-reply" />
            </button>
          )}
        </div>

        {yaRespondido && (
          <div className={`${styles.respuestaBox} ${esRespuestaPropia ? styles.mio : ''}`}>
            <span className={styles.respuestaLabel}>
              <i className="fa fa-reply" aria-hidden="true" /> {nombreRespuesta} respondió
            </span>
            {msg.textoRta && <p className={styles.texto}>{msg.textoRta}</p>}
            <Adjuntos adjunto={adjunto?.[`rta-${msg.idMensaje}`] ?? null} />
            <span className={styles.fecha}>{formatearFecha(msg.fechaEscritoRta, 'FH', 'hs')}</span>
          </div>
        )}

        {!yaRespondido && esVendedor && mensajeActivoId === msg.idMensaje && (
          <>
            {adjuntoPendiente?.[`resp-${msg.idMensaje}`]?.length > 0 && (
              <div className={styles.adjuntosPreview}>
                {adjuntoPendiente[`resp-${msg.idMensaje}`].map((a) => (
                  <div key={a.idAdjunto} className={styles.adjuntoThumb}>
                    {a.contentType?.startsWith('image/') ? (
                      <img src={a.url} alt={a.nombre} className={styles.thumbImg} />
                    ) : (
                      <span className={styles.thumbNombre}>{a.nombre}</span>
                    )}
                    <button
                      type="button"
                      className={styles.thumbRemove}
                      onClick={() =>
                        setAdjuntoPendiente((prev) => ({
                          ...prev,
                          [`resp-${msg.idMensaje}`]: prev[`resp-${msg.idMensaje}`].filter(
                            (x) => x.idAdjunto !== a.idAdjunto
                          )
                        }))
                      }
                      title="Quitar adjunto"
                    >
                      ×
                    </button>
                  </div>
                ))}
              </div>
            )}
            <div className={styles.inputPill}>
              <button
                type="button"
                className={styles.adjuntarBtn}
                onClick={() => setModalRespId(msg.idMensaje)}
                title="Adjuntar archivo"
              >
                <i className="fa fa-paperclip" />
              </button>
              <Input
                type="text"
                name={`reply-${msg.idMensaje}`}
                value={textosRespuesta[msg.idMensaje] || ''}
                autoFocus
                onChange={(e) =>
                  setTextosRespuesta((prev) => ({
                    ...prev,
                    [msg.idMensaje]: e.target.value
                  }))
                }
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault()
                    submitRespuesta(msg.idMensaje, msg)
                  }
                }}
                placeholder="Responder esta pregunta..."
                errores={errores}
              />
              <button
                type="button"
                className={styles.enviarBtn}
                disabled={
                  enviando ||
                  (!textosRespuesta[msg.idMensaje]?.trim() &&
                    !adjuntoPendiente?.[`resp-${msg.idMensaje}`]?.length)
                }
                onClick={() => submitRespuesta(msg.idMensaje, msg)}
                title="Responder"
              >
                <i className="fa fa-paper-plane" />
              </button>
            </div>
            <button
              type="button"
              className={styles.cancelarLink}
              onClick={() => setMensajeActivoId(null)}
            >
              Cancelar
            </button>
            <ModalUpload
              show={modalRespId === msg.idMensaje}
              onClose={() => setModalRespId(null)}
              idDoc={idPublicacion}
              idPerfil={usuario}
              idTipo={'10'}
              onCarga={(data) =>
                manejarAdjunto(data.data, setAdjuntoPendiente, `resp-${msg.idMensaje}`)
              }
              adjuntoPendiente={adjuntoPendiente?.[`resp-${msg.idMensaje}`] || []}
            />
          </>
        )}

        {!yaRespondido && mensajeActivoId !== msg.idMensaje && (
          <span className={styles.sinRespuesta}>
            <i className="fa fa-clock-o" aria-hidden="true" />
            Sin respuesta aún
          </span>
        )}
      </div>
    )
  }

  const agrupadoPorComprador =
    esVendedor && !idCompradorFiltro
      ? Object.values(
          mensajes.reduce((acc, msg) => {
            const key = msg.idComprador
            if (!acc[key]) acc[key] = { idComprador: key, comprador: msg.comprador, mensajes: [] }
            acc[key].mensajes.push(msg)
            return acc
          }, {})
        ).sort((a, b) => {
          const ultima = (grupo) =>
            Math.max(...grupo.mensajes.map((m) => new Date(m.fechaEscritoRta || m.fechaEscrito)))
          return ultima(b) - ultima(a)
        })
      : null
  return (
    <div className={styles.mensajeria}>
      {mensajes.length > 0 ? (
        agrupadoPorComprador ? (
          agrupadoPorComprador.map((grupo) => (
            <div key={grupo.idComprador} className={styles.grupo}>
              <div className={styles.grupoCabecera}>
                <span className={styles.avatar} aria-hidden="true">
                  {grupo.comprador?.trim()?.charAt(0)?.toUpperCase() || '?'}
                </span>
                <span className={styles.grupoNombre}>{grupo.comprador}</span>
                <span className={styles.grupoCantidad}>{grupo.mensajes.length}</span>
              </div>
              {grupo.mensajes.map((msg) => renderMensaje(msg))}
            </div>
          ))
        ) : (
          <div className={styles.lista}>{mensajes.map((msg) => renderMensaje(msg))}</div>
        )
      ) : (
        <div className={styles.vacio}>
          <i className="fa fa-comments-o" aria-hidden="true" />
          <p>{SIN_PREGUNTAS_TEXTO}</p>
        </div>
      )}

      {!esVendedor && idPublicacionEstado === ESTADO_PUBLICADA ? (
        <div className={styles.nuevaPreguntaBox}>
          {adjuntoPendiente?.['nueva']?.length > 0 && (
            <div className={styles.adjuntosPreview}>
              {adjuntoPendiente['nueva'].map((a) => (
                <div key={a.idAdjunto} className={styles.adjuntoThumb}>
                  {a.contentType?.startsWith('image/') ? (
                    <img src={a.url} alt={a.nombre} className={styles.thumbImg} />
                  ) : (
                    <span className={styles.thumbNombre}>{a.nombre}</span>
                  )}
                  <button
                    type="button"
                    className={styles.thumbRemove}
                    onClick={() =>
                      setAdjuntoPendiente((prev) => ({
                        ...prev,
                        nueva: prev['nueva'].filter((x) => x.idAdjunto !== a.idAdjunto)
                      }))
                    }
                    title="Quitar adjunto"
                  >
                    ×
                  </button>
                </div>
              ))}
            </div>
          )}
          <div className={styles.inputPill}>
            <button
              type="button"
              className={styles.adjuntarBtn}
              onClick={() => setShowModalNueva(true)}
              title="Adjuntar archivo"
            >
              <i className="fa fa-paperclip" />
            </button>
            <Input
              type="text"
              name="nuevaPregunta"
              value={textoPregunta}
              onChange={(e) => setTextoPregunta(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault()
                  submitPregunta()
                }
              }}
              placeholder="Escribí tu pregunta..."
              errores={errores}
            />
            <button
              type="button"
              className={styles.enviarBtn}
              disabled={enviando || (!textoPregunta.trim() && !adjuntoPendiente?.['nueva']?.length)}
              onClick={submitPregunta}
              title="Preguntar"
            >
              <i className="fa fa-paper-plane" />
            </button>
          </div>
          <ModalUpload
            show={showModalNueva}
            onClose={() => setShowModalNueva(false)}
            idDoc={idPublicacion}
            idPerfil={usuario}
            idTipo={'10'}
            onCarga={(data) => manejarAdjunto(data.data, setAdjuntoPendiente, 'nueva')}
            adjuntoPendiente={adjuntoPendiente?.['nueva'] || []}
          />
        </div>
      ) : null}
    </div>
  )
}

Mensajeria.propTypes = {
  mensajes: PropTypes.array.isRequired,
  setMensajes: PropTypes.func.isRequired,
  idPublicacion: PropTypes.number.isRequired,
  idVendedor: PropTypes.number,
  vendedorNombre: PropTypes.string,
  tipo: PropTypes.string,
  esVendedor: PropTypes.bool,
  idCompradorFiltro: PropTypes.number,
  idPublicacionEstado: PropTypes.number
}

export default Mensajeria
